import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/** Where an offer placement is displayed. Placements are one-per-surface. */
export enum OfferPlacementSurface {
  TRACKING_PAGE = "tracking-page",
  RETURNS_CONFIRMATION = "returns-confirmation",
  HOSTED_PAGE = "hosted-page",
  SHOPIFY_THANK_YOU = "shopify-thank-you",
}

export const prettyOfferPlacementSurface = {
  [OfferPlacementSurface.TRACKING_PAGE]: "Tracking page",
  [OfferPlacementSurface.RETURNS_CONFIRMATION]: "Returns confirmation",
  [OfferPlacementSurface.HOSTED_PAGE]: "Hosted page",
  [OfferPlacementSurface.SHOPIFY_THANK_YOU]: "Shopify thank-you page",
};

/** Falcon site audience enums; improve offer targeting (docs.falconlabs.com). */
export enum FalconAgeDemographic {
  AGE_18_24 = "AGE_18_24",
  AGE_25_34 = "AGE_25_34",
  AGE_35_44 = "AGE_35_44",
  AGE_45_54 = "AGE_45_54",
  AGE_55_64 = "AGE_55_64",
  AGE_65_PLUS = "AGE_65_PLUS",
}

export const prettyFalconAgeDemographic = {
  [FalconAgeDemographic.AGE_18_24]: "18–24",
  [FalconAgeDemographic.AGE_25_34]: "25–34",
  [FalconAgeDemographic.AGE_35_44]: "35–44",
  [FalconAgeDemographic.AGE_45_54]: "45–54",
  [FalconAgeDemographic.AGE_55_64]: "55–64",
  [FalconAgeDemographic.AGE_65_PLUS]: "65+",
};

export enum FalconGenderDemographic {
  MALE = "MALE",
  FEMALE = "FEMALE",
  MIXED = "MIXED",
}

export const prettyFalconGenderDemographic = {
  [FalconGenderDemographic.MALE]: "Mostly male",
  [FalconGenderDemographic.FEMALE]: "Mostly female",
  [FalconGenderDemographic.MIXED]: "Mixed",
};

const falconSiteSettingsSchema_ = z.object({
  ageDemographics: createEnum(Object.values(FalconAgeDemographic)).optional(),
  genderDemographics: createEnum(
    Object.values(FalconGenderDemographic),
  ).optional(),
  categoryName: z.string().optional(),
});
export type FalconSiteSettings = z.infer<typeof falconSiteSettingsSchema_>;
export const falconSiteSettingsSchema: z.ZodType<FalconSiteSettings> =
  falconSiteSettingsSchema_;

const roktOfferPlacementSchema_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  surface: createEnum(Object.values(OfferPlacementSurface)),
  /** Page identifier issued by the merchant's Rokt contact. */
  pageIdentifier: z.string(),
  sandbox: z.boolean(),
});
export const roktOfferPlacementSchema: z.ZodType<RoktOfferPlacement> =
  roktOfferPlacementSchema_;
export interface RoktOfferPlacement extends z.infer<
  typeof roktOfferPlacementSchema_
> {}

const falconOfferPlacementSchema_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  surface: createEnum(Object.values(OfferPlacementSurface)),
  /** Falcon's vendor placement id, returned by their placements API. */
  falconPlacementId: z.string(),
  /** false = Falcon serves mock/test offers (no revenue or attribution). */
  isLiveMode: z.boolean(),
});
export const falconOfferPlacementSchema: z.ZodType<FalconOfferPlacement> =
  falconOfferPlacementSchema_;
export interface FalconOfferPlacement extends z.infer<
  typeof falconOfferPlacementSchema_
> {}
